import { useEffect, useState, useRef } from 'react'

/**
 * React hook to detect whether an ad-blocker is active.
 *
 * Uses two strategies:
 *   1. A hidden bait <div> that many ad blockers will hide or remove via CSS filters.
 *   2. Attempting to load a resource based on URL and catching load errors.
 *
 * @param {{ testUrl?: string }} options
 * @param {string} [options.testUrl]
 *   URL of the resource to test (e.g. image, script, or iframe source)
 * @returns {[React.MutableRefObject<null>, boolean]}
 *   - baitRef: attach this ref to a hidden <div> for CSS-based detection.
 *   - detected: becomes true if an ad-blocker is inferred.
 *
 * @example
 * const [baitRef, adBlocked] = useAdBlocker({
 *   testUrl: 'https://content.aimatch.com/banner.jpg'
 * });
 * if (adBlocked) {
 *   // alternative flow
 * }
 * return <div ref={baitRef} style={{ position: 'absolute', top: '-999px', width: 1, height: 1 }} />;
 */
const useAdBlocker = ({ testUrl } = {}) => {
  const [detected, setDetected] = useState(false)
  const baitRef = useRef(null)

  useEffect(() => {
    let cleanupEl = null
    let timer = null

    // 1) CSS-based bait detection
    const bait = baitRef.current
    if (bait) {
      timer = setTimeout(() => {
        const style = window.getComputedStyle(bait)
        const isHidden =
          !document.body.contains(bait) ||
          style.display === 'none' ||
          bait.offsetParent === null
        if (isHidden) setDetected(true)
      }, 300)
    }

    // 2) Resource-based detection
    const url = typeof testUrl === 'string' ? testUrl : ''

    // Helper to attach element and record cleanup
    const attach = (element, parent = document.body) => {
      cleanupEl = element
      if (parent && element.tagName !== 'IMG') parent.appendChild(element)
    }

    if (url) {
      // Fallback by file extension
      const lower = url.toLowerCase()
      const imageExtRegex = /\.(jpe?g|png|gif|svg)(\?.*)?$/
      const match = imageExtRegex.exec(lower)
      if (match) {
        const img = new Image()
        img.src = url
        img.onload = () => {}
        img.onerror = () => setDetected(true)
        attach(img, null)
      } else if (lower.endsWith('.js')) {
        const script = document.createElement('script')
        script.src = url
        script.async = true
        script.onload = () => {}
        script.onerror = () => setDetected(true)
        attach(script)
      } else {
        const iframe = document.createElement('iframe')
        iframe.src = url
        iframe.style.display = 'none'
        iframe.onload = () => {}
        iframe.onerror = () => setDetected(true)
        attach(iframe)
      }
    }

    return () => {
      if (timer) clearTimeout(timer)
      if (cleanupEl) {
        if (cleanupEl.remove) cleanupEl.remove()
        else if (cleanupEl.parentNode)
          cleanupEl.parentNode.removeChild(cleanupEl)
      }
    }
  }, [testUrl])

  return [baitRef, detected]
}

export default useAdBlocker
